import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
// @ts-ignore
import moment from 'moment';
import FormControl from '../../../../Components/Form/FormControl';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import FileUpload from '../../../../Components/Base/FileUpload/FileUpload';
import apiClient from '../../../../Services/ApiClient';
import * as yup from 'yup';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useTranslation from '../../../../Hooks/useTranslation';
import InputField from '../../../../Components/Form/components/InputField';
import ECardFolderSelectField from '../../../Base/Components/Fields/ECardFolder/ECardFolderSelectField';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import FormCondition from '../../../../Components/Form/FormCondition';
import usePatientConsent from '../../../../Components/PatientProfile/Hooks/usePatientConsent';
import PatientNoConsentProvided from '../../../Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import { ECardType } from '../../Enums/ECardType.enum';

interface Props {
	patientId: string;
	targetPatientId?: string;
	onSuccess?: (needReload: boolean, folderId: string) => void;
	params?: any;
	eCardType: ECardType;
}

interface FileUploadData {
	folderId: string;
	patientId: string;
	fileName: string;
}

const ECardAddFile: React.FC<Props> = ({
	targetPatientId,
	patientId,
	onSuccess,
	eCardType,
}) => {
	const { t } = useTranslation();
	const [selectedFile, setSelectedFile] = useState(null);
	const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
	const { isConsentProvided } = usePatientConsent(patientId);

	const uploadFiles = async ({
		folderId,
		patientId,
		fileName,
	}: FileUploadData) => {
		const fileId = uuidv4();
		const documentationUrl =
			'/patients/' + patientId + '/documentation/' + folderId + '/' + fileId;

		await apiClient.post(documentationUrl, {
			folderId: folderId,
			name: fileName,
		});

		// @ts-ignore
		const formData = new FormData();
		formData.append('files[]', selectedFile);
		await apiClient.postMultipart(documentationUrl + '/upload', formData);
	};

	const handleSave = async (values: any) => {
		setIsSubmitDisabled(true);
		await uploadFiles({
			folderId: values.category.value,
			fileName: values.name,
			patientId: patientId,
		});

		if (values.uploadToPatientECard && targetPatientId) {
			await uploadFiles({
				folderId: values.eCardCategory.value,
				fileName: values.name,
				patientId: targetPatientId,
			});
		}

		setIsSubmitDisabled(false);
		onSuccess?.(true, values.category.value);
	};

	const handleChangeFile = (event: any) => {
		setSelectedFile(event.target.files && event.target.files[0]);
	};

	const validationSchema = yup.object({
		name: yup.string().required(t('base.shared.filename')),
		category: yup.object().shape({
			value: yup.string().uuid().required(t('base.shared.category')),
		}),
		file: yup
			.mixed()
			.required(t('base.shared.document'))
			.test(
				'value',
				t('base.shared.document') ?? '',
				value => value && value.length,
			),
		uploadToPatientECard: yup.boolean(),
		eCardCategory: yup.object().when('uploadToPatientECard', {
			is: true,
			then: yup.object().shape({
				value: yup
					.string()
					.uuid()
					.required(t('component.eCard.pickPatientECardCategory')),
			}),
		}),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	useEffect(() => {
		if (isConsentProvided && eCardType === ECardType.HEALTH_DOCUMENTATION) {
			formMethods.reset({ uploadToPatientECard: isConsentProvided });
		}
	}, [isConsentProvided]);

	return (
		<FormWrapper formMethods={formMethods} registerForChanges>
			<InputField
				label='base.shared.filename'
				name='name'
				className='mb-2'
				isRequired
			/>
			<ECardFolderSelectField
				patientId={patientId}
				name='category'
				label='base.shared.category'
				className='mb-2'
				isRequired
			/>
			<FormControl
				label={'base.shared.document'}
				name={'file'}
				onChange={handleChangeFile}
				isRequired={true}>
				<FileUpload />
			</FormControl>
			{isConsentProvided && targetPatientId ? (
				<>
					<CheckboxField
						name='uploadToPatientECard'
						tx={
							eCardType === ECardType.HEALTH_DOCUMENTATION
								? 'component.eCard.addToPatientsECard'
								: 'component.eCard.addToHealthDocumentation'
						}
						className='mb-3'
					/>
					<FormCondition controlName='uploadToPatientECard' equal={true}>
						<ECardFolderSelectField
							patientId={targetPatientId}
							name='eCardCategory'
							label={
								eCardType === ECardType.HEALTH_DOCUMENTATION
									? 'component.eCard.pickPatientECardCategory'
									: 'component.eCard.pickHealthDocumentationCategory'
							}
							className='mb-2'
							isRequired
						/>
					</FormCondition>
				</>
			) : (
				<PatientNoConsentProvided patientId={patientId} />
			)}
			<div className={'mt-4'}>
				<SubmitButton
					title={'base.shared.save'}
					onSubmit={handleSave}
					disabled={isSubmitDisabled}
				/>
			</div>
		</FormWrapper>
	);
};

export default ECardAddFile;
